import { BuilderCompContextValue, BuilderPage, ElementSetting, TypographySelectValue } from '@makyoapp/makyo-common'

import { PAGINATION_CONSTANTS } from '../constants'
import {
  BuilderComponentSetting,
  BuilderComponentSettingType,
  ComponentSettingKey,
  FlattenedItem,
  SpacingInputUnit,
} from '../types'
import { LABELS } from '../types/labels'

type SpacingSettings = {
  max: {
    [SpacingInputUnit.Pixel]: number
    [SpacingInputUnit.Rem]: number
    [SpacingInputUnit.Em]: number
    [SpacingInputUnit.Percent]: number
  }
}

type DefaultValue = number | { x: number; y: number }

interface SpacingConfig {
  id: ComponentSettingKey
  spacingSettings?: SpacingSettings
  defaultValue?: DefaultValue
}

export function makeSpacing(
  {
    padding,
    margin,
    headlineMargin,
    paddingBadge,
  }: {
    padding?: SpacingConfig
    margin?: SpacingConfig
    headlineMargin?: SpacingConfig
    paddingBadge?: SpacingConfig
  },
  extra: BuilderComponentSetting[] = []
) {
  const items: BuilderComponentSetting[] = []

  const addSpacingItem = (label: string, config?: SpacingConfig) => {
    if (!config) return

    const defaultValue = config.defaultValue
    const isNum = typeof defaultValue === 'number'
    const x = isNum ? defaultValue : (defaultValue?.x ?? 0)
    const y = isNum ? defaultValue : (defaultValue?.y ?? 0)

    items.push({
      type: BuilderComponentSettingType.Spacing,
      label,
      spacingSettings: config.spacingSettings,
      defaultValue: {
        top: { value: y, unit: 'px' },
        right: { value: x, unit: 'px' },
        bottom: { value: y, unit: 'px' },
        left: { value: x, unit: 'px' },
      },
      id: config.id,
    })
  }

  addSpacingItem('Padding', padding)
  addSpacingItem('Margin', margin)
  addSpacingItem('Headline Margin', headlineMargin)
  addSpacingItem('Padding Badge', paddingBadge)

  return {
    items: [...items, ...extra],
  }
}

export function makeTypography(id: ComponentSettingKey, defaultValue: string) {
  return {
    items: [
      {
        id,
        label: 'Typography',
        defaultValue: {
          typography: defaultValue,
        },
        type: BuilderComponentSettingType.TypographySelect,
      },
      {
        id: `${id}-custom`,
        label: 'Style',
        type: BuilderComponentSettingType.TypographyGroup,
      },
    ],
  }
}

export function makeTypographyGroup(
  id: ComponentSettingKey,
  items: { id: ComponentSettingKey; label: string; defaultValue: string }[]
) {
  const it = [],
    vals: Record<string, TypographySelectValue> = {}

  for (const { id, label, defaultValue } of items) {
    it.push({
      id,
      label,
    })
    vals[id] = {
      typography: defaultValue,
    }
  }

  return {
    items: [
      {
        id,
        label: 'Typography',
        defaultValue: vals,
        type: BuilderComponentSettingType.TypographyGroup,
        typographyGroupSettings: {
          items: it,
        },
      },
    ],
  }
}

export function makeScheme(id: ComponentSettingKey, withLabel = true) {
  return {
    id,
    label: withLabel ? 'Color Scheme' : '',
    type: BuilderComponentSettingType.ColorScheme,
  }
}

export function makeSchemeItems(id: ComponentSettingKey, withLabel = false) {
  return {
    items: [makeScheme(id, withLabel)],
  }
}

export function makeSchemeVariant(
  id: ComponentSettingKey,
  schemeId: ComponentSettingKey,
  withLabel: boolean | string = true,
  defaultValue: string = 'primary',
  fullColor: 'bg' | 'fg' | 'both' = 'both',
  label?: string
) {
  return {
    id,
    type: BuilderComponentSettingType.ColorVariant,
    label: withLabel === true ? (label ?? 'Color Variant') : typeof withLabel === 'string' ? withLabel : '',
    defaultValue,
    colorVariantSettings: {
      schemeId,
      fullColor,
    },
  }
}

export function makeWidth(id: ComponentSettingKey, withLabel = true, defaultValue = 100, minValue = 0, help = '') {
  return {
    type: BuilderComponentSettingType.Slider,
    label: withLabel ? LABELS.WIDTH_KEY : '',
    help,
    id,
    defaultValue: [defaultValue],
    sliderSettings: {
      max: 100,
      min: minValue,
      step: 1,
      range: false,
      unit: '%',
      showUnit: true,
    },
  }
}

function makeClampWidth(
  id: ComponentSettingKey,
  options?: {
    defaultValue?: number
    minValue?: number
    help?: string
    step?: number
    label?: string
    defaultUnit?: '%' | 'px'
    showUnit?: boolean
  }
) {
  return {
    type: BuilderComponentSettingType.Slider,
    label: options?.label ?? LABELS.WIDTH_KEY,
    help: options?.help ?? '',
    id,
    defaultValue: [options?.defaultValue ?? 100],
    clampInAllDevices: true,
    sliderSettings: {
      max: Infinity,
      min: options?.minValue ?? 1,
      step: options?.step ?? 1,
      range: false,
      unit: options?.defaultUnit ?? '%',
      showUnit: options?.showUnit ?? true,
    },
  }
}

export function makeWidthItems(id: ComponentSettingKey, withLabel = true, defaultValue = 100, minValue = 0, help = '') {
  return {
    items: [makeWidth(id, withLabel, defaultValue, minValue, help)],
  }
}

export function makeClampWidthItems(
  id: ComponentSettingKey,
  options?: {
    defaultValue?: number
    minValue?: number
    help?: string
    label?: string
    defaultUnit?: '%' | 'px'
    showUnit?: boolean
  }
) {
  return {
    items: [makeClampWidth(id, options)],
  }
}

function makeClampHeight(
  id: ComponentSettingKey,
  options?: {
    defaultValue?: number
    minValue?: number
    help?: string
    step?: number
    label?: string
    defaultUnit?: '%' | 'px' | 'vh'
    showUnit?: boolean
  }
) {
  return {
    type: BuilderComponentSettingType.Slider,
    label: options?.label ?? LABELS.HEIGHT_KEY,
    help: options?.help ?? '',
    id,
    defaultValue: [options?.defaultValue ?? 100],
    clampInAllDevices: true,
    sliderSettings: {
      max: Infinity,
      min: options?.minValue ?? 1,
      step: options?.step ?? 1,
      range: false,
      unit: options?.defaultUnit ?? 'px',
      showUnit: options?.showUnit ?? true,
    },
  }
}

export function makeClampHeightItems(
  id: ComponentSettingKey,
  options?: {
    defaultValue?: number
    minValue?: number
    help?: string
    label?: string
    defaultUnit?: '%' | 'px' | 'vh'
    showUnit?: boolean
  }
) {
  return {
    items: [makeClampHeight(id, options)],
  }
}

// Fungsi untuk membuat width/height mode selector dengan opsi full viewport
export function makeDimensionModeItems(
  modeId: ComponentSettingKey,
  valueId: ComponentSettingKey,
  options?: {
    modeLabel?: string
    valueLabel?: string
    defaultMode?: 'auto' | 'custom' | 'full-viewport'
    defaultValue?: number
    defaultUnit?: '%' | 'px' | 'vh'
    minValue?: number
    help?: string
  }
) {
  const modeOptions = [
    { value: 'auto', label: 'Auto' },
    { value: 'custom', label: 'Custom' },
    { value: 'full-viewport', label: 'Full Viewport' },
  ]

  return {
    items: [
      {
        id: modeId,
        type: BuilderComponentSettingType.Select,
        label: options?.modeLabel ?? 'Mode',
        defaultValue: options?.defaultMode ?? 'auto',
        selectSettings: {
          options: modeOptions,
        },
      },
      {
        id: valueId,
        type: BuilderComponentSettingType.Slider,
        label: options?.valueLabel ?? 'Value',
        help: options?.help ?? '',
        defaultValue: [options?.defaultValue ?? 100],
        clampInAllDevices: true,
        sliderSettings: {
          max: Infinity,
          min: options?.minValue ?? 1,
          step: 1,
          range: false,
          unit: options?.defaultUnit ?? '%',
          showUnit: true,
        },
        dependOn: {
          id: modeId,
          showWhen: (v: string) => v === 'custom',
        },
      },
    ],
  }
}

export function makeDataSource(id: ComponentSettingKey, help?: string, label?: string) {
  return {
    id,
    label: label ?? 'Data Source',
    type: BuilderComponentSettingType.TableSelect,
    help,
    tableSelectSettings: {
      includeSnapshot: true,
    },
  }
}

export function makeNumItems(id: ComponentSettingKey, max = 100, label = 'Dataset Limit') {
  return {
    id,
    label,
    type: BuilderComponentSettingType.Number,
    defaultValue: 10,
    numberSettings: { max },
  }
}

export function makeSorting(
  sortBy: ComponentSettingKey,
  sortType: ComponentSettingKey,
  tableSourceId: ComponentSettingKey,
  defaultValue = 'asc'
) {
  return {
    items: [
      {
        id: sortBy,
        label: 'Sort By',
        type: BuilderComponentSettingType.SortBySelect,
        sortBySelectSettings: {
          tableSourceId,
        },
      },
      {
        id: sortType,
        label: 'Sort Ordering',
        defaultValue,
        type: BuilderComponentSettingType.IconSelect,
        iconSelectSettings: {
          options: [
            {
              id: 'asc',
              title: 'Ascending',
              icon: 'Asc16',
            },
            {
              id: 'desc',
              title: 'Descending',
              icon: 'Desc16',
            },
          ],
        },
        dependOn: {
          id: sortBy,
          showWhen: (v) => v !== '#random#',
        },
      } as BuilderComponentSetting,
    ],
  }
}

export function makeFilter(id: ComponentSettingKey, tableSourceId: ComponentSettingKey) {
  return {
    items: [
      {
        id,
        label: 'Filter',
        type: BuilderComponentSettingType.ColumnFilter,
        columnFilterSettings: {
          tableSourceId,
        },
      },
    ],
  }
}

export function makeUserControlledFilter(id: ComponentSettingKey, tableSourceId: ComponentSettingKey) {
  return {
    items: [
      {
        id,
        label: 'Fields',
        type: BuilderComponentSettingType.UserColumnFilter,
        columnFilterSettings: {
          tableSourceId,
        },
      },
    ],
  }
}

export function makeValueChangeCb(labelKey: ComponentSettingKey) {
  return (
    nextVal: string | string[] | undefined,
    elId: string | undefined,
    elementSetting: ElementSetting,
    page: BuilderPage,
    flattenedElements: FlattenedItem[],
    ancestors: FlattenedItem[],
    ctx: BuilderCompContextValue
  ) => {
    // todo
    // const formParent = ancestors.find((a) => a.compId === 'form')
    // const tableId = formParent
    //   ? ctx.page?.elementSettings[formParent.id]?.[ComponentSettingKey.FormDataSource]
    //   : undefined
    // const col = ctx.dbTables.find((tb) => tb.id === tableId)?.columns.find((c) => c.id === (nextVal as string))
    // if (col && elId) {
    //   // @ts-ignore
    //   elementSetting[elId][labelKey] = col.name
    //   const idx = flattenedElements.findIndex((e) => e.id === elId)
    //   if (idx !== -1) {
    //     flattenedElements[idx].customTitle = col.name
    //     page.elements = buildTree(flattenedElements)
    //   }
    // }
  }
}

export function makePagination(id: ComponentSettingKey) {
  return {
    items: [
      {
        id,
        type: BuilderComponentSettingType.Select,
        label: LABELS.DEFAULT_ITEMS_PER_PAGE_KEY,
        defaultValue: PAGINATION_CONSTANTS.DEFAULT_PER_PAGE,
        selectSettings: {
          options: PAGINATION_CONSTANTS.DEFAULT_PER_PAGE_OPTIONS,
        },
      },
    ],
  }
}

export function makeTextColorSettings(
  colorTypeId: ComponentSettingKey,
  solidColorId: ComponentSettingKey | undefined,
  startGradientId: ComponentSettingKey | undefined,
  endGradientId: ComponentSettingKey | undefined,
  gradientDirectionId: ComponentSettingKey | undefined,
  schemeId: ComponentSettingKey,
  label: string = 'Text Color',
  defaultValue: string = 'adaptive',
  options?: {
    textColorOptions?: Array<{ value: string; label: string }>
    showSolid?: boolean
    showGradient?: boolean
    showAdaptive?: boolean
    defaultSolidColor?: string
    defaultStartGradient?: string
    defaultEndGradient?: string
    defaultGradientDirection?: string
  }
) {
  // Default text color options
  const defaultTextColorOptions = [
    { value: 'adaptive', label: 'Adaptive' },
    { value: 'solid', label: 'Solid' },
    { value: 'gradient', label: 'Gradient' },
  ]

  // Filter options based on configuration
  let textColorOptions = defaultTextColorOptions
  if (options?.textColorOptions) {
    textColorOptions = options.textColorOptions
  } else {
    const filteredOptions = []
    if (options?.showAdaptive !== false) {
      filteredOptions.push({ value: 'adaptive', label: 'Adaptive' })
    }
    if (options?.showSolid !== false) {
      filteredOptions.push({ value: 'solid', label: 'Solid' })
    }
    if (options?.showGradient !== false) {
      filteredOptions.push({ value: 'gradient', label: 'Gradient' })
    }
    textColorOptions = filteredOptions
  }

  // If no options are available, return empty items
  if (textColorOptions.length === 0) {
    return { items: [] }
  }

  const gradientColorDirectionOptions = [
    { id: 'to-right', icon: 'ArrowRight16', title: 'To Right' },
    { id: 'to-left', icon: 'ArrowLeft16', title: 'To Left' },
    { id: 'to-top', icon: 'ArrowUp16', title: 'To Top' },
    { id: 'to-bottom', icon: 'ArrowDown16', title: 'To Bottom' },
  ]

  const items: BuilderComponentSetting[] = [
    {
      id: colorTypeId,
      label: `Color Type`,
      type: BuilderComponentSettingType.Select,
      selectSettings: {
        options: textColorOptions,
      },
      defaultValue,
    },
  ]

  // Add solid color option only if 'solid' is in the available options
  if (textColorOptions.some((opt) => opt.value === 'solid')) {
    items.push({
      id: solidColorId ?? '',
      label: `Solid Color`,
      type: BuilderComponentSettingType.ColorVariant,
      colorVariantSettings: {
        fullColor: 'fg' as const,
        schemeId,
      },
      defaultValue: options?.defaultSolidColor ?? 'primary-40',
      dependOn: {
        id: colorTypeId,
        showWhen: (v: unknown) => v === 'solid',
      },
    })
  }

  // Add gradient options only if 'gradient' is in the available options
  if (textColorOptions.some((opt) => opt.value === 'gradient')) {
    items.push(
      {
        id: startGradientId ?? '',
        label: `Start Gradient`,
        type: BuilderComponentSettingType.ColorVariant,
        colorVariantSettings: {
          fullColor: 'fg' as const,
          schemeId,
        },
        defaultValue: options?.defaultStartGradient ?? 'primary-40',
        dependOn: {
          id: colorTypeId,
          showWhen: (v: unknown) => v === 'gradient',
        },
      },
      {
        id: endGradientId ?? '',
        label: `End Gradient`,
        type: BuilderComponentSettingType.ColorVariant,
        colorVariantSettings: {
          fullColor: 'fg' as const,
          schemeId,
        },
        defaultValue: options?.defaultEndGradient ?? 'primary-80',
        dependOn: {
          id: colorTypeId,
          showWhen: (v: unknown) => v === 'gradient',
        },
      },
      {
        id: gradientDirectionId ?? '',
        label: `Gradient Direction`,
        type: BuilderComponentSettingType.IconSelect,
        iconSelectSettings: {
          options: gradientColorDirectionOptions,
        },
        defaultValue: options?.defaultGradientDirection ?? 'to-right',
        dependOn: {
          id: colorTypeId,
          showWhen: (value?: unknown) => {
            return value === 'gradient'
          },
        },
      }
    )
  }

  return { items }
}

export function getDataSourceKeyForComponent(groupId: string): ComponentSettingKey | null {
  const groupIdToSettingKey: Record<string, ComponentSettingKey> = {
    accordion: ComponentSettingKey.AccordionDataSource,
    'array-gallery': ComponentSettingKey.ArrayGalleryDataSource,
    'bar-chart': ComponentSettingKey.BarChartDataSource,
    card: ComponentSettingKey.CardDataSource,
    comment: ComponentSettingKey.CommentDataSource,
    form: ComponentSettingKey.FormDataSource,
    gallery: ComponentSettingKey.GalleryDataSource,
    list: ComponentSettingKey.ListDataSource,
    'data-list': ComponentSettingKey.ListDataSource,
    menu: ComponentSettingKey.MenuDataSource,
    'multi-location': ComponentSettingKey.MultiLocationDataSource,
    'pie-chart': ComponentSettingKey.PieChartDataSource,
    selection: ComponentSettingKey.SelectionDataOptionsSource,
    'shopping-cart': ComponentSettingKey.ShoppingCartDataSource,
    'data-table': ComponentSettingKey.TableDataSource,
    timeline: ComponentSettingKey.TimelineDataSource,
    calendar: ComponentSettingKey.CalendarDataSource,
  }

  return groupIdToSettingKey[groupId] || null
}

export const hoverEffectOptions = [
  { value: 'none', label: 'None' },
  { value: 'underline', label: 'Underline' },
  { value: 'lift', label: 'Lift Up' },
  { value: 'scale', label: 'Scale' },
  { value: 'dim', label: 'Dim' },
  { value: 'skew', label: 'Skew' },
  { value: 'rotate', label: 'Rotate' },
  { value: 'glow', label: 'Glow' },
  { value: 'shimmer', label: 'Shimmer' },
  { value: 'border', label: 'Border Pulse' },
  { value: 'squeeze', label: 'Squeeze' },
  { value: 'blur', label: 'Blur Zoom' },
  { value: 'neon', label: 'Neon Pulse' },
  { value: 'flip', label: 'Flip' },
  { value: 'bounce', label: 'Bounce' },
]
